import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { gql, useApolloClient, useMutation } from '@apollo/client';
import { Spinner } from 'equasion-lib';
import ApiError from '@/lib/shared-coms/ApiError';
import { useRootStore } from '@/lib/providers/RootProvider';
import PurchaseOrderReceive from './PurchaseOrderReceive';
import type { ReceiveList, ReceiveScanJob, ReceiveScanResult } from './receiveScan';

// One selection for the list, shared by the query and every mutation that answers with it. Plain
// strings rather than a fragment so scripts/gql-frontend-validate.cjs resolves them against the schema.
const RECEIVE_LIST_FIELDS = `
  purchaseOrderId
  clientPurchaseOrderNumber
  vendorName
  clientStatus
  lines {
    productId
    product {
      id
      title
      fdaProduct {
        id
        name
        elevenDigitNdc
        deaSchedule
      }
      upcProduct {
        id
        upc
      }
    }
    productName
    ndc
    deaSchedule
    orderedQuantity
    shippedQuantity
    epcisOverageQuantity
    status
    serialNumber
    gtin
    manualEntry
    confirmedAt
    confirmedByName
  }
  confirmedCount
  openCount
  notInShipmentCount
  canComplete
  completeBlockedReason
`;

export const PURCHASE_ORDER_RECEIVE_LIST = gql`
  query purchaseOrderReceiveList($companyId: ID!, $purchaseOrderId: ID!) {
    purchaseOrderReceiveList(companyId: $companyId, purchaseOrderId: $purchaseOrderId) {
      ${RECEIVE_LIST_FIELDS}
    }
  }
`;

export const PURCHASE_ORDER_RECEIVE_SCAN = gql`
  mutation purchaseOrderReceiveScan(
    $companyId: ID!
    $purchaseOrderId: ID!
    $serialNumber: String!
    $gtin: String
    $manualEntry: Boolean
  ) {
    purchaseOrderReceiveScan(
      companyId: $companyId
      purchaseOrderId: $purchaseOrderId
      serialNumber: $serialNumber
      gtin: $gtin
      manualEntry: $manualEntry
    ) {
      outcome
      productId
      productName
      ndc
      list {
        ${RECEIVE_LIST_FIELDS}
      }
    }
  }
`;

export const PURCHASE_ORDER_RECEIVE_RESET = gql`
  mutation purchaseOrderReceiveResetLine(
    $companyId: ID!
    $purchaseOrderId: ID!
    $productId: ID!
  ) {
    purchaseOrderReceiveResetLine(
      companyId: $companyId
      purchaseOrderId: $purchaseOrderId
      productId: $productId
    ) {
      ${RECEIVE_LIST_FIELDS}
    }
  }
`;

export const PURCHASE_ORDER_RECEIVE_COMPLETE = gql`
  mutation purchaseOrderReceiveComplete($companyId: ID!, $purchaseOrderId: ID!) {
    purchaseOrderReceiveComplete(companyId: $companyId, purchaseOrderId: $purchaseOrderId) {
      receivedUnitCount
      purchaseOrderNumber
    }
  }
`;

function PurchaseOrderReceiveData() {
  const { purchaseOrderId = '' } = useParams();
  const companyId = useRootStore((state) => state.selectedCompany?.id);
  const client = useApolloClient();
  const [scanUnit] = useMutation(PURCHASE_ORDER_RECEIVE_SCAN);
  const [resetLine] = useMutation(PURCHASE_ORDER_RECEIVE_RESET);
  const [completeReceive] = useMutation(PURCHASE_ORDER_RECEIVE_COMPLETE);

  const [list, setList] = useState<ReceiveList | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);

  // Newest-request-wins. A scan that started before the one already on screen must not roll the
  // checklist back.
  const requestSeqRef = useRef(0);
  const appliedSeqRef = useRef(0);
  const applyList = useCallback((seq: number, next: ReceiveList | null | undefined) => {
    if (!next || seq < appliedSeqRef.current) return;
    appliedSeqRef.current = seq;
    setList(next);
  }, []);

  const loadList = useCallback(async () => {
    if (!companyId || !purchaseOrderId) return;
    const seq = ++requestSeqRef.current;
    try {
      const { data } = await client.query({
        query: PURCHASE_ORDER_RECEIVE_LIST,
        variables: { companyId, purchaseOrderId },
        fetchPolicy: 'network-only'
      });
      applyList(seq, data?.purchaseOrderReceiveList);
      setLoadError(null);
    } catch (error) {
      console.error('purchaseOrderReceiveList failed:', error);
      setLoadError((current) => current ?? error);
    }
  }, [applyList, client, companyId, purchaseOrderId]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  const onScan = useCallback(
    async (job: ReceiveScanJob): Promise<ReceiveScanResult> => {
      const seq = ++requestSeqRef.current;
      const { data } = await scanUnit({
        variables: {
          companyId,
          purchaseOrderId,
          serialNumber: job.serialNumber,
          gtin: job.gtin,
          manualEntry: job.manualEntry
        }
      });
      const result = data?.purchaseOrderReceiveScan;
      applyList(seq, result?.list);
      return {
        outcome: result?.outcome,
        productId: result?.productId,
        productName: result?.productName,
        ndc: result?.ndc
      };
    },
    [applyList, companyId, purchaseOrderId, scanUnit]
  );

  const onReset = useCallback(
    async (productId: string) => {
      const seq = ++requestSeqRef.current;
      const { data } = await resetLine({
        variables: { companyId, purchaseOrderId, productId }
      });
      applyList(seq, data?.purchaseOrderReceiveResetLine);
    },
    [applyList, companyId, purchaseOrderId, resetLine]
  );

  const onComplete = useCallback(async () => {
    const { data } = await completeReceive({
      variables: { companyId, purchaseOrderId }
    });
    const result = data?.purchaseOrderReceiveComplete;
    return {
      receivedUnitCount: result?.receivedUnitCount ?? 0,
      purchaseOrderNumber: result?.purchaseOrderNumber
    };
  }, [companyId, completeReceive, purchaseOrderId]);

  if (!companyId) return <Spinner />;
  if (loadError && !list) return <ApiError error={loadError} />;
  if (!list) return <Spinner />;

  return (
    <PurchaseOrderReceive
      list={list}
      onScan={onScan}
      onReset={onReset}
      onComplete={onComplete}
    />
  );
}

export default PurchaseOrderReceiveData;
